import React, { useState, useEffect, useRef } from 'react';
import { AutoComplete, Typography } from 'antd';
import { useDebounce } from '@/utility/useDebounce';
import { useNavigate } from 'react-router-dom';

const { Text } = Typography;

interface SearchResult {
  id: string;
  title: string;
  description: string;
  url: string;
}

interface GlobalSearchProps {
  placeholder?: string;
  style?: React.CSSProperties;
  className?: string;
}

const GlobalSearch: React.FC<GlobalSearchProps> = ({ 
  placeholder = "Search...", 
  className 
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const debouncedSearchValue = useDebounce(searchValue, 300);

  const searchTypesense = async (query: string): Promise<SearchResult[]> => {
    if (!query.trim()) return [];
    
    try {

      
      const response = await fetch(
        `https://engine.babymomcompany.com/collections/sellco_global_search/documents/search?q=${encodeURIComponent(query)}&query_by=title,description&per_page=10`,
        {
          method: 'GET',
          headers: {
            'X-TYPESENSE-API-KEY': import.meta.env.VITE_TYPESENSE_API_KEY,
            'Content-Type': 'application/json',
          },
          mode: 'cors',
        }
      );


      if (!response.ok) {
        return [];
      }

      const data = await response.json();

      
      if (data.hits && data.hits.length > 0) {
        const results = data.hits.map((hit: any) => ({
          id: hit.document.id,
          title: hit.document.title,
          description: hit.document.description,
          url: hit.document.url,
        }));
        return results;
      }
      
      return [];
    } catch (error) {
      return [];
    }
  };

  // Perform search when debounced value changes
  useEffect(() => {
    const performSearch = async () => {

      
      if (debouncedSearchValue.trim()) {
        setIsLoading(true);
        try {
          const results = await searchTypesense(debouncedSearchValue);
          setSearchResults(results);
          setIsOpen(results.length > 0); // Only open if we have results
        } catch (error) {
          setSearchResults([]);
          setIsOpen(false);
        } finally {
          setIsLoading(false);
        }
      } else {
        setSearchResults([]);
        setIsLoading(false);
        setIsOpen(false);
      }
    };

    performSearch();
  }, [debouncedSearchValue]);

  // Handle search input change
  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    setIsOpen(value.length > 0);
  };

  // Handle result selection
  const handleSelect = (value: string) => {
    const result = searchResults.find(r => r.id === value);
    
    if (result) {
      
      // Check if it's an internal URL (contains app.sellco.ai)
      if (result.url.includes('https://app.sellco.ai/')) {
        // Extract the path from the full URL for internal navigation
        const url = new URL(result.url);
        const path = url.pathname;
        
        // Try both navigation methods
        try {
          navigate(path);
        } catch (error) {
          // Fallback to window.location
          window.location.href = result.url;
        }
      } else {
        // External URL - open in new tab
        window.open(result.url, '_blank');
      }
      
      setIsOpen(false);
      setSearchValue('');
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Generate autocomplete options
  const autocompleteOptions = searchResults.map((result) => ({
    value: result.id,
    label: (
      <div 
        style={{ padding: '8px 0', cursor: 'pointer' }}
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
        
          setIsOpen(false);
          setSearchValue('');
        
          if (result.url.startsWith('http')) {
            window.open(result.url, '_blank');
          } else {
            navigate(`/${result.url}`);
          }
        }}
        
      >
        <div style={{ fontWeight: 500, marginBottom: '4px', color: '#ff9900e6' }}>
          {result.title}
        </div>
        <Text type="secondary" style={{ fontSize: '12px' }}>
          {result.description}
        </Text>
      </div>
    ),
  }));

  return (
    <div ref={searchRef} style={{ position: 'relative', width: '100%', maxWidth: '400px', minWidth: '300px' }}>
      <AutoComplete
        value={searchValue}
        options={autocompleteOptions}
        onSelect={handleSelect}
        onChange={handleSearchChange}
        onSearch={handleSearchChange}
        open={isOpen}
        placeholder={placeholder}
        style={{ width: '100%', minWidth: '300px' }}
        className={className}
        notFoundContent={
          isLoading ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <Text type="secondary">Searching...</Text>
            </div>
          ) : searchValue && searchResults.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <Text type="secondary">No results found</Text>
            </div>
          ) : null
        }
      />
    </div>
  );
};

export default GlobalSearch;
